import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Check, Coins, TrendingUp, Loader2 } from "lucide-react";
import { useApp } from "@/app/AppLayout";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import api, { apiErr } from "@/lib/api";

const FEATURE_LABELS = {
  message: "Messages", deep_analysis: "Analyses approfondies", scientific_search: "Recherches",
  openalex_crossref: "OpenAlex / Crossref", long_writing: "Co-rédaction", pdf_analysis: "Analyses PDF",
  bibliography: "Bibliographie", similarity: "Similarité",
};

export default function Billing() {
  const { setCredits, refreshMeta } = useApp();
  const { refresh } = useAuth();
  const [plans, setPlans] = useState([]);
  const [current, setCurrent] = useState(null);
  const [usage, setUsage] = useState(null);
  const [busy, setBusy] = useState(null);

  const load = async () => {
    const [p, b, u] = await Promise.all([api.get("/plans"), api.get("/billing/me"), api.get("/billing/usage")]);
    setPlans(p.data.plans); setCurrent(b.data.plan); setUsage(u.data);
  };
  useEffect(() => { load(); }, []);

  const subscribe = async (key) => {
    setBusy(key);
    try {
      const { data } = await api.post("/billing/subscribe", { plan: key });
      toast.success(`Offre ${data.plan.name} activée`); setCredits(data.credits);
      await refresh(); await refreshMeta(); await load();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(null); }
  };

  return (
    <div className="max-w-5xl mx-auto px-6 py-8 animate-fade-up">
      <h1 className="font-serif text-3xl font-bold text-navy">Abonnement & crédits</h1>
      <p className="text-muted2 mt-1">Gérez votre offre et suivez votre consommation.</p>

      <Tabs defaultValue="plans" className="mt-6">
        <TabsList><TabsTrigger value="plans" data-testid="tab-plans">Offres</TabsTrigger><TabsTrigger value="usage" data-testid="tab-usage">Mon utilisation</TabsTrigger></TabsList>

        <TabsContent value="plans" className="mt-6">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
            {plans.map((p) => {
              const isCurrent = current?.key === p.key;
              return (
                <div key={p.key} className={`relative bg-white rounded-2xl border p-5 flex flex-col ${p.popular ? "border-lisungi-yellow ring-1 ring-lisungi-yellow" : "border-[#E5E7EB]"}`} data-testid={`billing-plan-${p.key}`}>
                  {isCurrent && <span className="absolute -top-3 right-4 bg-navy text-white text-xs font-bold px-3 py-1 rounded-full">Actuel</span>}
                  <h3 className="font-semibold text-navy">{p.name}</h3>
                  <div className="mt-2 flex items-baseline gap-1"><span className="font-serif text-3xl font-bold text-navy">{p.price === null ? "Devis" : `${p.price}${p.currency}`}</span>{p.price > 0 && <span className="text-xs text-muted2">{p.period}</span>}</div>
                  <p className="text-xs font-mono text-muted2 mt-1">{p.credits ? `${p.credits} crédits/mois` : "Personnalisé"}</p>
                  <ul className="mt-4 space-y-2 flex-1">{p.features.map((f) => <li key={f} className="flex items-start gap-2 text-xs text-ink"><Check className="h-3.5 w-3.5 text-lisungi-yellow mt-0.5 shrink-0" />{f}</li>)}</ul>
                  <Button onClick={() => subscribe(p.key)} disabled={isCurrent || busy === p.key || p.key === "institution"} className={`mt-4 rounded-full ${p.popular ? "bg-navy text-white hover:bg-navy-800" : "bg-white border border-navy text-navy hover:bg-navy hover:text-white"}`} data-testid={`subscribe-${p.key}`}>
                    {busy === p.key ? <Loader2 className="h-4 w-4 animate-spin" /> : isCurrent ? "Offre actuelle" : p.key === "institution" ? "Sur devis" : "Choisir"}
                  </Button>
                </div>
              );
            })}
          </div>
          <p className="text-xs text-muted2 mt-4 text-center">Paiement simulé — l'intégration Stripe / PayPal et moyens locaux sera activée ultérieurement.</p>
        </TabsContent>

        <TabsContent value="usage" className="mt-6">
          {usage && (
            <>
              <div className="grid md:grid-cols-3 gap-4">
                <div className="bg-navy text-white rounded-2xl p-5"><div className="flex items-center gap-2 text-white/70 text-sm"><Coins className="h-4 w-4 text-lisungi-yellow" /> Solde</div><p className="font-serif text-4xl font-bold mt-2">{usage.credits}</p><p className="text-xs text-white/50">crédits disponibles</p></div>
                <div className="bg-white rounded-2xl border border-[#E5E7EB] p-5"><p className="text-sm text-muted2">Consommés ce mois</p><p className="font-serif text-4xl font-bold text-navy mt-2">{usage.total_spent}</p><Progress value={usage.used_pct} className="h-1.5 mt-3" /><p className="text-xs text-muted2 mt-1">{usage.used_pct}% de {usage.monthly_credits}</p></div>
                <div className="bg-white rounded-2xl border border-[#E5E7EB] p-5"><p className="text-sm text-muted2">Par fonctionnalité</p>
                  <div className="mt-3 space-y-1.5">{Object.entries(usage.by_feature).map(([k, v]) => <div key={k} className="flex justify-between text-xs"><span className="text-muted2">{FEATURE_LABELS[k] || k}</span><span className="font-mono font-semibold text-navy">{v}</span></div>)}
                    {Object.keys(usage.by_feature).length === 0 && <p className="text-xs text-muted2">Aucune consommation.</p>}</div>
                </div>
              </div>
              {usage.used_pct >= 75 && (
                <div className="mt-4 bg-lisungi-yellow/15 border border-lisungi-yellow rounded-xl p-4 flex items-center gap-3 text-sm text-navy"><TrendingUp className="h-5 w-5" />Vous avez utilisé {usage.used_pct}% de vos crédits. Pensez à passer à une offre supérieure.</div>
              )}
              <div className="mt-5 bg-white rounded-2xl border border-[#E5E7EB] p-5">
                <p className="font-semibold text-navy mb-3">Historique récent</p>
                <div className="space-y-1.5 max-h-80 overflow-y-auto">
                  {usage.history.map((h) => (
                    <div key={h.id} className="flex items-center justify-between text-sm border-b border-[#F1F1F1] py-1.5">
                      <span className="text-muted2">{FEATURE_LABELS[h.feature] || h.feature}</span>
                      <span className={`font-mono font-semibold ${h.amount < 0 ? "text-red-500" : "text-green-600"}`}>{h.amount > 0 ? "+" : ""}{h.amount}</span>
                    </div>
                  ))}
                  {usage.history.length === 0 && <p className="text-sm text-muted2">Aucune activité.</p>}
                </div>
              </div>
            </>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
